import React, {StrictMode} from 'react';

import {createRoot} from 'react-dom/client';

import MojitoCore from 'mojito/core';

import {loadStyle} from '#core/utils/utils.js';
import {absoluteUrlToInternalResource} from '#core/utils/url-utils.js';
import {ALLOWED_ROLES} from '#core/utils/data-validation.cjs';

import {fetchTranslationsFile} from '#core/localization.js';
import {AbstractFeature, allFeatures} from '#core/application/abstract-feature.js';

import {uiActions} from './store/ui-store.js';
import {loadDescriptions} from './store/descriptions';
import {setMozaicHostElement} from './hooks/mozaicHostElement.ts';

const {dispatch} = MojitoCore.Services.redux.store;

class MozaicEditorFeature extends AbstractFeature {
    get name() {
        return 'Mozaic Editor';
    }

    updateUserRole({isAdmin, userRole = ALLOWED_ROLES.DEVELOPER}) {
        const role = isAdmin ? ALLOWED_ROLES.CMS_ADMIN : userRole;
        dispatch(uiActions.updateUserRole(role));
    }

    updateVisibility(isVisible) {
        dispatch(uiActions.updateIsVisible(isVisible));
    }

    isCMS(isCMS) {
        dispatch(uiActions.isCMS(!!isCMS));
    }

    show(params = {}) {
        if (!CSS.supports('selector(&)')) {
            const message =
                'CSS nesting is not supported in this browser 🦖🦖🦖. Please use modern browser to run Mozaic';
            this.logger.error(message);
            window.alert(message);

            return;
        }
        const containerId = 'dbx-mozaic-host';
        let host = document.getElementById(containerId);
        this.updateUserRole(params);
        this.isCMS(params.isAdmin);
        if (!host) {
            host = document.createElement('DIV');
            host.id = containerId;
            document.body.appendChild(host);
            const shadow = host.attachShadow({mode: 'open', delegatesFocus: true});
            setMozaicHostElement(shadow);

            loadStyle(absoluteUrlToInternalResource('mozaic/mozaic.css'), shadow);

            const supplementaryContainerId = `${PRODUCT_NAME}-supplementary`;
            const supplementaryContainer = document.getElementById(supplementaryContainerId);

            supplementaryContainer && shadow.appendChild(supplementaryContainer.cloneNode(true));

            const reactContainer = document.createElement('DIV');
            reactContainer.id = 'dbx-mozaic';
            shadow.appendChild(reactContainer);

            let translations = null;
            const availableDictionaries = AVAILABLE_DICTIONARIES;
            const trPromises = Promise.all(availableDictionaries.map(lang => fetchTranslationsFile(lang)))
                .then(translationsList => {
                    translations = translationsList.reduce(
                        (acc, translations, index) => ({
                            ...acc,
                            [availableDictionaries[index]]: translations,
                        }),
                        {}
                    );
                })
                .catch(({message} = {}) => {
                    this.logger.error(`Fetching of translations failed: ${message}`);
                });

            Promise.all([trPromises, loadDescriptions()]).then(() => {
                return import(
                    /* webpackChunkName: "dbx.mozaic" */
                    './controller/controller-view.jsx'
                )
                    .then(module => {
                        module.setTranslations(translations);

                        return module.MozaicControllerView;
                    })
                    .then(MozaicControllerView => {
                        const root = createRoot(reactContainer);
                        const component = React.createElement(
                            StrictMode,
                            {translations},
                            React.createElement(MozaicControllerView)
                        );
                        root.render(component);
                        this.updateVisibility(true);
                    });
            });

            // retargetEvents(shadow);
        } else {
            this.updateVisibility(true);
        }
    }

    hide() {
        this.updateVisibility(false);
    }
}

export const MozaicEditor = new MozaicEditorFeature(allFeatures);

window.mozaic = params => {
    MozaicEditor.show(params);
};
